package fyi.moko.hotpl.domain.service

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * Lets an account switch hold sync still while it rewrites local ownership.
 *
 * Signing into an existing social account changes the uid, and
 * [fyi.moko.hotpl.data.service.SyncCoordinator] reacts to that immediately: it attaches a
 * Firestore snapshot listener for the new uid and starts draining the push queue. That races
 * [fyi.moko.hotpl.domain.usecase.LinkAccountUseCase], which is still re-branding the anonymous
 * session's places onto the new uid — the incoming remote dataset could overwrite a local place
 * before it has been remapped out of the way, and a half-rebranded queue could push places under
 * the wrong owner.
 *
 * While [isPaused] is true the coordinator detaches its listener and stops pushing; it picks the
 * (now settled) uid back up on resume.
 */
class SyncGate {
    private val _isPaused = MutableStateFlow(false)
    val isPaused: StateFlow<Boolean> = _isPaused.asStateFlow()

    /** Runs [block] with sync paused, resuming even if it fails. */
    suspend fun <T> withPause(block: suspend () -> T): T {
        _isPaused.value = true
        try {
            return block()
        } finally {
            _isPaused.value = false
        }
    }
}
